import React, { useState, useEffect, useCallback } from 'react'
import { Typography, Box, Alert, Skeleton, Button, Grid, Paper, Divider } from '@mui/material'
import { SidebarLayout } from '@/components/Layout'
import { ProfileFormComponent } from '@/components/Profile/ProfileForm'
import { LoadingSpinner, ErrorMessage } from '@/components/common'
import { RatingSummaryWidget } from '@/components/Reviews/RatingSummaryWidget'
import { ReviewDisplayComponent } from '@/components/Reviews/ReviewDisplayComponent'
import { useAuth } from '@/contexts/AuthContext'
import { User } from '@/types'
import { Review, ReviewResponseData } from '@/types/review-types'
import { ProfileService } from '@/utils/profile-service'
import { apiClient } from '@/utils/api-client'

export default function Profile() {
  const { userProfile, isAuthenticated, isLoading: authLoading, refreshUser } = useAuth()
  const [profile, setProfile] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [guestReviews, setGuestReviews] = useState<Review[]>([])
  const [reviewsLoading, setReviewsLoading] = useState(false)
  const [reviewsError, setReviewsError] = useState<string | null>(null)

  const loadProfile = useCallback(async () => {
    if (!isAuthenticated || !userProfile) {
      setIsLoading(false)
      return
    }

    try {
      setError(null)
      setIsLoading(true)
      
      console.log('Profile page: Loading profile data...')
      
      // Try to fetch the latest profile from the API
      const latestProfile = await ProfileService.getProfile()
      setProfile(latestProfile)
      console.log('Profile page: Successfully loaded profile from API')
    } catch (err: any) {
      console.warn('Profile page: Failed to fetch latest profile, using cached data:', err)
      
      // Check if it's an auth error
      if (err?.status === 401 || err?.code === 'UNAUTHORIZED') {
        console.error('Profile page: Authentication error detected')
        setError('Authentication failed. Please try refreshing the page.')
      } else {
        // If API call fails for other reasons, fall back to the profile from auth context
        console.log('Profile page: Using cached profile data from auth context')
        setProfile(userProfile)
      }
    } finally {
      setIsLoading(false)
    }
  }, [isAuthenticated, userProfile])

  const loadGuestReviews = useCallback(async () => {
    if (!userProfile?.userId) return

    setReviewsLoading(true)
    setReviewsError(null)

    try {
      // In a real implementation, this would fetch reviews where the user is the reviewee (guest)
      // For now, we'll use mock data
      const mockGuestReviews: Review[] = [
        {
          reviewId: 'guest_rev_1',
          bookingId: 'book_1',
          reviewerType: 'host',
          reviewerId: 'host_1',
          revieweeId: userProfile.userId,
          overallRating: 5,
          categoryRatings: {
            guestCommunication: 5,
            guestCleanliness: 5,
            houseRulesAdherence: 5,
          },
          reviewText: 'Excellent guest! Very respectful, clean, and followed all house rules. Would definitely host again.',
          reviewerName: 'John (Host)',
          reviewerPhotoUrl: undefined,
          isPublic: false, // Host reviews of guests are private
          status: 'active',
          createdAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
          updatedAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
          reviewDeadline: new Date(Date.now() + 20 * 24 * 60 * 60 * 1000).toISOString(),
        },
        {
          reviewId: 'guest_rev_2',
          bookingId: 'book_2',
          reviewerType: 'host',
          reviewerId: 'host_2',
          revieweeId: userProfile.userId,
          overallRating: 4,
          categoryRatings: {
            guestCommunication: 4,
            guestCleanliness: 4,
            houseRulesAdherence: 5,
          },
          reviewText: 'Great guest overall. Good communication and left the place clean. Minor delay in check-out but nothing major.',
          reviewerName: 'Sarah (Host)',
          reviewerPhotoUrl: undefined,
          isPublic: false,
          status: 'active',
          createdAt: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString(),
          updatedAt: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString(),
          reviewDeadline: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000).toISOString(),
          response: {
            responseId: 'guest_resp_1',
            reviewId: 'guest_rev_2',
            responderId: userProfile.userId,
            responseText: 'Thank you for the feedback! I apologize for the slight delay in check-out. I\'ll be more mindful of timing in the future.',
            status: 'active',
            createdAt: new Date(Date.now() - 19 * 24 * 60 * 60 * 1000).toISOString(),
            updatedAt: new Date(Date.now() - 19 * 24 * 60 * 60 * 1000).toISOString(),
            responseDeadline: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(),
          },
        },
      ]

      setGuestReviews(mockGuestReviews)
    } catch (err) {
      console.error('Error loading guest reviews:', err)
      setReviewsError(err instanceof Error ? err.message : 'Failed to load reviews')
    } finally {
      setReviewsLoading(false)
    }
  }, [userProfile?.userId])

  const handleReviewResponse = async (reviewId: string, responseData: ReviewResponseData) => {
    try {
      await apiClient.post(`/reviews/${reviewId}/response`, responseData)
      
      // Update the local state to show the response
      setGuestReviews(prevReviews => 
        prevReviews.map(review => 
          review.reviewId === reviewId 
            ? {
                ...review,
                response: {
                  responseId: `resp_${Date.now()}`,
                  reviewId,
                  responderId: userProfile?.userId || '',
                  responseText: responseData.responseText,
                  status: 'active',
                  createdAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString(),
                  responseDeadline: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
                }
              }
            : review
        )
      )
    } catch (err) {
      console.error('Error submitting review response:', err)
      throw err
    }
  }

  useEffect(() => {
    // Only load profile when auth is fully ready and user is authenticated
    if (!authLoading && isAuthenticated && userProfile) {
      console.log('Profile page: Auth ready, loading profile...')
      loadProfile()
      loadGuestReviews()
    } else if (!authLoading) {
      console.log('Profile page: Auth not ready or user not authenticated')
      setIsLoading(false)
    }
  }, [authLoading, isAuthenticated, userProfile, loadProfile, loadGuestReviews])

  const handleProfileUpdate = async (updatedProfile: User) => {
    setProfile(updatedProfile)
    // Refresh the auth context to update the cached user data
    await refreshUser()
  }

  const handleRetry = () => {
    setError(null)
    loadProfile()
  }

  if (authLoading || isLoading) {
    return (
      <SidebarLayout>
        <Box sx={{ mb: 4 }}>
          <Skeleton variant="text" width={200} height={48} />
          <Skeleton variant="text" width={400} height={24} />
        </Box>
        <LoadingSpinner />
      </SidebarLayout>
    )
  }

  if (!isAuthenticated) {
    return (
      <SidebarLayout>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h3" component="h1" gutterBottom>
            Profile
          </Typography>
        </Box>
        <Alert severity="warning">
          Please log in to view and manage your profile.
        </Alert>
      </SidebarLayout>
    )
  }

  if (error && !profile) {
    return (
      <SidebarLayout>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h3" component="h1" gutterBottom>
            Profile
          </Typography>
        </Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
          <Box sx={{ mt: 2 }}>
            <Button variant="outlined" onClick={handleRetry}>
              Try Again
            </Button>
          </Box>
        </Alert>
      </SidebarLayout>
    )
  }

  return (
    <SidebarLayout>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          Profile
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your account information and preferences
        </Typography>
      </Box>

      {profile && (
        <Grid container spacing={4}>
          {/* Profile Form */}
          <Grid item xs={12}>
            <ProfileFormComponent
              user={profile}
              onProfileUpdate={handleProfileUpdate}
            />
          </Grid>

          {/* Guest Reviews Section */}
          <Grid item xs={12}>
            <Paper sx={{ p: 4 }}>
              <Typography variant="h5" gutterBottom>
                Reviews from Hosts
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                See what hosts have said about your stays
              </Typography>

              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={4}>
                {/* Rating Summary */}
                <Grid item xs={12} lg={4}>
                  <RatingSummaryWidget
                    entityId={profile.userId}
                    entityType="guest"
                    showBreakdown={true}
                    loading={reviewsLoading}
                    error={reviewsError || undefined}
                  />
                </Grid>

                {/* Reviews List */}
                <Grid item xs={12} lg={8}>
                  {reviewsError && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                      {reviewsError}
                    </Alert>
                  )}

                  <ReviewDisplayComponent
                    reviews={guestReviews}
                    showPagination={true}
                    showResponses={true}
                    allowResponse={true}
                    currentUserId={profile.userId}
                    loading={reviewsLoading}
                    error={reviewsError || undefined}
                    onSubmitResponse={handleReviewResponse}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      )}
    </SidebarLayout>
  )
}